import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  cn,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  ProjectTaskListItem,
  TaskStatus,
} from "@/modules/management/project/domain/types";
import { taskStatusProgressPercentTextClass } from "@/modules/management/project/presentation/lib/task-status-progress-styles";
import { TaskProgressBar } from "@/modules/management/project/presentation/ui/TaskProgressBar";
import { getInitials } from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { confirm, toast } from "@devhop/ui";
import { managementProjectApi } from "../api/client";
import { useProjectTasks } from "../api/queries";
import { TaskFormDialog } from "./TaskFormDialog";

function formatDate(s: string | null) {
  if (!s) return "—";
  try {
    return format(new Date(s), "dd MMM yyyy");
  } catch {
    return s;
  }
}

function taskStatusLabel(status: TaskStatus): string {
  switch (status) {
    case "pending":
      return "ລໍຖ້າ";
    case "in_progress":
      return "ກຳລັງດຳເນີນງານ";
    case "done":
      return "ສຳເລັດ";
    default:
      return status;
  }
}

function taskStatusClass(status: TaskStatus): string {
  switch (status) {
    case "in_progress":
      return "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30";
    case "done":
      return "bg-green-500/15 text-green-700 dark:text-green-300 border-green-500/30";
    default:
      return "bg-muted text-muted-foreground";
  }
}

function priorityLabel(p: string | null): string {
  switch (p) {
    case "high":
      return "ສູງ";
    case "medium":
      return "ກາງ";
    case "low":
      return "ຕ່ຳ";
    default:
      return "—";
  }
}

export function ProjectDetailTasksTab({
  projectId,
  projectLevel = "organization",
  projectPositionId,
  projectDepartmentId,
  canUpdate,
  openTaskDialogRequested = false,
  onOpenTaskDialogConsumed,
}: {
  projectId: string;
  projectLevel?: "position" | "department" | "organization";
  projectPositionId?: string;
  projectDepartmentId?: string;
  canUpdate: boolean;
  openTaskDialogRequested?: boolean;
  onOpenTaskDialogConsumed?: () => void;
}) {
  const queryClient = useQueryClient();
  const tasksQuery = useProjectTasks(projectId);
  const tasks = tasksQuery.data ?? [];
  const [taskDialog, setTaskDialog] = useState<{
    open: boolean;
    task?: ProjectTaskListItem;
  }>({ open: false });

  useEffect(() => {
    if (openTaskDialogRequested && canUpdate) {
      setTaskDialog({ open: true });
      onOpenTaskDialogConsumed?.();
    }
  }, [openTaskDialogRequested, canUpdate, onOpenTaskDialogConsumed]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ["management", "projects", projectId, "tasks"],
    });
    queryClient.invalidateQueries({
      queryKey: ["management", "projects", projectId],
    });
  };

  const handleDeleteTask = async (task: ProjectTaskListItem) => {
    const ok = await confirm({
      title: "ລຶບວຽກຍ່ອຍ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການລຶບ "${task.title}"? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້.`,
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await managementProjectApi.deleteTask(projectId, task.id);
      toast.success("ລຶບວຽກຍ່ອຍສຳເລັດ");
      invalidate();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ",
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canUpdate && (
          <Button
            size="sm"
            onClick={() => setTaskDialog({ open: true })}
            className="gap-1.5"
          >
            <Plus className="h-4 w-4" />
            ເພີ່ມວຽກຍ່ອຍ
          </Button>
        )}
      </div>
      <div className="rounded-xl border bg-card">
        {tasksQuery.isLoading ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            ກຳລັງໂຫຼດ...
          </div>
        ) : tasks.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-sm">
            ບໍ່ມີວຽກຍ່ອຍ
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ຊື່ວຽກ</TableHead>
                <TableHead>ຜູ້ຮັບຜິດຊອບ</TableHead>
                <TableHead>ວັນສົ່ງ</TableHead>
                <TableHead>% ຄວາມຄືບໜ້າ</TableHead>
                <TableHead>ສະຖານະ</TableHead>
                <TableHead>ຄວາມສຳຄັນ</TableHead>
                {canUpdate && <TableHead className="w-[100px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {tasks.map((task) => (
                <TableRow key={task.id}>
                  <TableCell className="font-medium">{task.title}</TableCell>
                  <TableCell>
                    {task.assigneeId ? (
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarImage
                            src={
                              resolveImageSrc(
                                task.assigneeImage ?? undefined,
                              ) ?? undefined
                            }
                            alt={task.assigneeName ?? ""}
                          />
                          <AvatarFallback className="text-xs">
                            {getInitials(task.assigneeName ?? task.assigneeId)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="max-w-[120px] truncate text-muted-foreground text-sm">
                          {task.assigneeName ?? "—"}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(task.dueDate)}
                  </TableCell>
                  <TableCell>
                    <div className="flex min-w-[80px] max-w-[120px] items-center gap-2">
                      <TaskProgressBar
                        status={task.status}
                        value={task.progressPercent}
                        className="h-2 min-w-0 flex-1"
                      />
                      <span
                        className={cn(
                          "shrink-0 text-xs tabular-nums",
                          taskStatusProgressPercentTextClass(task.status),
                        )}
                      >
                        {task.progressPercent}%
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={taskStatusClass(task.status)}
                    >
                      {taskStatusLabel(task.status)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {priorityLabel(task.priority)}
                  </TableCell>
                  {canUpdate && (
                    <TableCell className="w-[100px]">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => setTaskDialog({ open: true, task })}
                          aria-label="ແກ້ໄຂ"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteTask(task)}
                          aria-label="ລຶບ"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {tasks.length > 0 && (
          <div className="border-t px-4 py-2 text-muted-foreground text-xs">
            ສະແດງ {tasks.length} ວຽກຍ່ອຍ
          </div>
        )}
      </div>

      <TaskFormDialog
        projectId={projectId}
        projectLevel={projectLevel}
        projectPositionId={projectPositionId}
        projectDepartmentId={projectDepartmentId}
        task={taskDialog.task}
        open={taskDialog.open}
        onOpenChange={(open) => setTaskDialog((p) => ({ ...p, open }))}
        onSuccess={invalidate}
      />
    </div>
  );
}
